import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Tab, Tabs } from '@material-ui/core';
import zoomSdk from '@zoom/appssdk';
import {
    getZoomAppInsights, getZoomAppLiveMeetings, getZoomAppUser
} from './apis/model-results/api';
import { Feature, ZoomAppUser } from './apis/model-results/models';
import AuthRequest from './AuthRequest';
import FeatureCard from './FeatureCard';
import { useStyles } from './styles/globals';
import { getDeviceAuthToken } from './utils/auth';
import { eventConfig, eventHighlightDuration, tabConfig } from './utils/config';

const App: FC = () => {
  const classes = useStyles();
  const deviceAuthToken = useRef(getDeviceAuthToken());
  const zoomAppUserPullingInterval = useRef<NodeJS.Timer>();
  const liveMeetingsPullingInterval = useRef<NodeJS.Timer>();
  const featuresPullingInterval = useRef<NodeJS.Timer>();
  const [verifyingAuth, setVerifyingAuth] = useState(true);
  const [zoomAppUser, setZoomAppUser] = useState<ZoomAppUser>();
  const [liveMeetingIds, setLiveMeetingIds] = useState<string[]>([]);
  const [speakerName, setSpeakerName] = useState<string>();
  const [tab, setTab] = useState<keyof typeof tabConfig>('home');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const timeout = setTimeout(() => {
      zoomSdk
        .config({ capabilities: ['getMeetingContext', 'getUserContext', 'showNotification'] })
        .then(() => {
          zoomSdk
            .getUserContext()
            .then(({ screenName }) => setSpeakerName(screenName))
            .catch((err) => setError(`getUserContext: ${JSON.stringify(err, null, 2)}`));
        })
        .catch((err) => setError(`config: ${JSON.stringify(err, null, 2)}`));
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    zoomAppUserPullingInterval.current = setInterval(() => {
      getZoomAppUser(deviceAuthToken.current)
        .then(({ data }) => {
          setZoomAppUser(data);
          clearInterval(zoomAppUserPullingInterval.current);
        })
        .catch(() => {})
        .finally(() => setVerifyingAuth(false));
    }, 1000);
    return () => clearInterval(zoomAppUserPullingInterval.current);
  }, []);

  useEffect(() => {
    if (!zoomAppUser?.user_id) return;
    liveMeetingsPullingInterval.current = setInterval(() => {
      getZoomAppLiveMeetings(deviceAuthToken.current)
        .then(({ data }) => {
          if (!data.length) return;
          setLiveMeetingIds(data);
          clearInterval(liveMeetingsPullingInterval.current);
        })
        .catch((err) => setError(`getZoomAppLiveMeetings: ${JSON.stringify(err, null, 2)}`));
    }, 10000);
    return () => clearInterval(liveMeetingsPullingInterval.current);
  }, [zoomAppUser]);

  useEffect(() => {
    if (!liveMeetingIds.length || !speakerName) return;
    featuresPullingInterval.current = setInterval(() => {
      getZoomAppInsights(deviceAuthToken.current, liveMeetingIds, speakerName)
        .then(({ data }) => data.length && setFeatures(data))
        .catch((err) => setError(`getZoomAppInsights: ${JSON.stringify(err, null, 2)}`));
    }, 1000);
    return () => clearInterval(featuresPullingInterval.current);
  }, [liveMeetingIds, speakerName]);

  const realtimeFeatures = features.filter(({ event }) => eventConfig[event]?.tab === 'home');
  const highlightedFeature =
    realtimeFeatures.length &&
    moment.utc(realtimeFeatures[0].timestamp).isAfter(moment.utc().subtract(eventHighlightDuration))
      ? realtimeFeatures[0]
      : null;

  return (
    <div className={classes.app}>
      <Tabs
        value={tab}
        onChange={(_, tab) => setTab(tab)}
        action={(node) => node?.updateIndicator()}
      >
        {Object.entries(tabConfig).map(([key, { label }]) => (
          <Tab key={`tab-${key}`} className={classes.appTab} value={key} label={label} />
        ))}
      </Tabs>
      <div className={classes.appContent}>
        {speakerName && zoomAppUser?.user_id && !features.length && (
          <span className={classes.appTitle}>
            Hello {speakerName}, you are logged in as <i>{zoomAppUser?.user_id}</i>
          </span>
        )}
        {!verifyingAuth &&
          (!zoomAppUser?.user_id ? (
            <AuthRequest deviceAuthToken={deviceAuthToken.current} />
          ) : !liveMeetingIds.length ? (
            <span className={classes.appTitle}>
              Waiting for Talka Zoom-bot to join and start extracting insights...
            </span>
          ) : highlightedFeature ? (
            <FeatureCard feature={highlightedFeature} />
          ) : tab === 'home' ? (
            <div className={classes.appCard}>
              <span className={classes.appText}>
                Talka-highlighted insights will be displayed here as they are detected...
              </span>
            </div>
          ) : null)}
        {(verifyingAuth || (zoomAppUser?.user_id && !features.length)) && (
          <img className={classes.appLogo} src="/talka-insights-logo.png" />
        )}
        {/* {error && <span className={classes.appTitle}>Error fetching insights: {error}</span>} */}
        {tab !== 'home' &&
          features
            .filter(({ event }) => eventConfig[event]?.tab === tab)
            .map((feature) => (
              <FeatureCard
                key={`feature-${feature.event}-${feature.timestamp}`}
                feature={feature}
                defaultExpanded
              />
            ))}
      </div>
    </div>
  );
};

export default App;
